import React, {
  CSSProperties,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { DropdownProps } from "semantic-ui-react";

import moment from "moment";
import ReactTable, { RowInfo } from "react-table-6";
import withFixedColumns from "react-table-hoc-fixed-columns";

import DropdownOptions from "react-lib/appcon/common/DropdownOptions";

import SnackMessage from "react-lib/apps/common/SnackMessage";

import CardSelectAppointmentDatetimeUX from "./CardSelectAppointmentDatetimeUX";
import DeviceTimeSlot, { DeviceSlotType } from "./DeviceTimeSlot";
import ModMakeAppointmentRehab from "./ModMakeAppointmentRehab";

import { RehabDeviceSlotType, RehabPlanType, SelectRehabDeviceSlotType } from "./RHBInterface";

import { beToAd, formatDate, formatDateToYYYYMMDD } from "react-lib/utils/dateUtils";

import "./TableTreatment.scss";

const ReactTableFixedColumns = withFixedColumns(ReactTable);

type CardSelectAppointmentDatetimeProps = {
  onEvent: (e: any) => any;
  setProp: (key: string, value: any, callback?: () => void) => any;
  proxyController: any;
  checkedAppointmentList: Record<string, any>[];
  columnWidth?: number;
  divisionId: any;
  fixedColumnWidth?: number;
  hideCombine?: boolean;
  hideCreateNew?: boolean;
  hideHeader?: boolean;
  rehabDeviceSlot?: RehabDeviceSlotType;
  rehabOrderAppointmentList?: Record<string, any>[];
  rehabTreatmentList?: RehabPlanType[];
  selectedRehabDeviceSlot?: SelectRehabDeviceSlotType;
  showSelectDivision?: boolean;
  tableStyle?: CSSProperties;
  buttonLoadCheck: any;
  errorMessage: any;
  languageUX: string;
  searchedItemListWithKey: any;
  selectedPatient: any;
  divisionOptions?: any[];
  onClose?: () => void;
  onSuccess: (data: { appointment: any }) => void;
};

type FilterType = {
  appointment?: number | null;
  date: string;
  devices: number[];
  division?: number;
  isDeviceFree?: boolean;
  type: "COMBINE" | "CREATE_NEW";
};

const currentDate = formatDate(moment());

const DEFAULT_FILTER: FilterType = {
  date: currentDate,
  devices: [],
  type: "CREATE_NEW",
};

const BUTTON_ACTIONS = {
  select_combine: "SELECT_COMBINE",
};

const CARD_SELECT_APP_DATETIME = "CardSelectAppointmentDatetime";

const ACTION_SELECT_COMBINE = `${CARD_SELECT_APP_DATETIME}_${BUTTON_ACTIONS.select_combine}`;

const CardSelectAppointmentDatetime = (props: CardSelectAppointmentDatetimeProps) => {
  const [filter, setFilter] = useState<FilterType>({ ...DEFAULT_FILTER });
  const [selectedDevices, setSelectedDevices] = useState<DeviceSlotType[]>([]);
  const [openModMakeAppointment, setOpenModMakeAppointment] = useState<boolean>(false);

  useEffect(() => {
    if (filter.division) {
      props.onEvent({
        message: "getRehabDeviceSlot",
        params: { date: filter.date, division: filter.division },
      });
    }
  }, [filter.date, filter.division]);

  useEffect(() => {
    setSelectedDevices([]);
    setFilter({ ...DEFAULT_FILTER });
  }, []);

  useEffect(() => {
    setFilter((prevFilter) => ({ ...prevFilter, division: props.divisionId }));
  }, [props.divisionId]);

  useEffect(() => {
    if (!props.selectedRehabDeviceSlot) {
      return;
    }

    const data = props.selectedRehabDeviceSlot;

    const filteredDevices = data.devices.filter((device) => !device.device_free);

    setFilter((prevFilter) => ({
      ...prevFilter,
      date: formatDate(moment(data.date)),
      devices: filteredDevices.map((device) => device.rehab_device_id),
      division: data.division,
      isDeviceFree: data.devices.some((device) => device.device_free),
      type: "COMBINE",
    }));

    setSelectedDevices(
      data.devices.map((device) => ({
        id: device.id,
        device: device.rehab_device_id,
        device_free: device.device_free,
        end_datetime: `${device.end_date} ${device.end_time}`,
        rehabilitation_order_id: device.rehabilitation_order_id,
        start_datetime: `${device.start_date} ${device.start_time}`,
      }))
    );
  }, [props.selectedRehabDeviceSlot]);

  const handleSelectDevice = useCallback(
    (data: DeviceSlotType) => {
      let selected = [...selectedDevices];

      const index = selected.findIndex(
        (item) =>
          item.device === data.device &&
          moment(item.start_datetime).isSame(data.start_datetime) &&
          moment(item.end_datetime).isSame(data.end_datetime)
      );

      selected = selected.filter((item) => item.device !== data.device);

      if (index === -1) {
        setSelectedDevices([...selected, data]);
      } else {
        selected.splice(index, 1);

        setSelectedDevices([...selected]);
      }
    },
    [selectedDevices]
  );

  const filteredDevices = useMemo(() => {
    const allDevices = [...(props.rehabDeviceSlot?.devices ?? [])];

    const result = allDevices.filter((device) => filter.devices.includes(device.id));

    const freeDevice = allDevices.find((device) => device.device_free);

    if (filter.isDeviceFree && freeDevice) {
      return [...result, freeDevice];
    } else if (!filter.isDeviceFree && freeDevice) {
      return result.filter((device) => !device.device_free);
    }

    return result;
  }, [filter.devices, filter.isDeviceFree, props.rehabDeviceSlot?.devices]);

  const formattedDevices = useMemo(
    () =>
      filteredDevices.map((item) => {
        const isExpired = moment(item.inactive_date).isBefore(beToAd(filter.date), "days");
        const isInactive = isExpired && !item.active;

        return { ...item, isInactive };
      }),
    [filteredDevices]
  );

  const enabledDeviceFree = useMemo(
    () => (props.rehabDeviceSlot?.devices ?? []).some((item) => item.device_free),
    [props.rehabDeviceSlot?.devices]
  );

  const deviceOptions = useMemo(() => {
    const devices = [...(props.rehabDeviceSlot?.devices ?? [])];

    const filtered = devices.filter((item) => !item.device_free);

    return filtered.map((item) => ({
      key: item.id,
      text: item.name,
      value: item.id,
    }));
  }, [props.rehabDeviceSlot]);

  const appointmentOptions = useMemo(() => {
    const currentDateAD = moment().format("YYYY-MM-DD");
    const filteredAppointments = (props.rehabOrderAppointmentList ?? []).filter(
      (appointment) => appointment.appointment_date >= currentDateAD
    );

    return filteredAppointments.map((item) => ({
      key: item.id,
      original: item,
      text: `${formatDate(moment(item.appointment_date))}, Physiotherapist : ${
        item.physiotherapist_fullname || "Unspecified"
      }`,
      value: item.id,
    }));
  }, [props.rehabOrderAppointmentList]);

  const columns = useMemo(() => {
    const times = filteredDevices.flatMap((item) => [item.start_time, item.end_time] as string[]);
    const sorted = [...times].sort((a, b) => a.localeCompare(b));

    // Handle empty case
    if (sorted.length === 0) {
      return [];
    }

    // Parse the start and end times
    const startTime = sorted[0];
    const endTime = sorted.slice(-1)[0];

    const startMoment = moment(`${formatDateToYYYYMMDD(filter.date, "-", true)} ${startTime}`);
    const endMoment = moment(`${formatDateToYYYYMMDD(filter.date, "-", true)} ${endTime}`);

    const diffHours = Math.max(0, endMoment.diff(startMoment, "hours"));
    const slots = Math.max(8, diffHours);

    const timeArray: string[] = [];

    for (let index = 0; index < slots; index++) {
      const slotStartTime = startMoment.clone().add(index, "hours");
      const formattedTime = slotStartTime.format("HH:mm");
      const nextHourTime = slotStartTime.clone().add(1, "hour").format("HH:mm");

      timeArray.push(`${formattedTime} - ${nextHourTime}`);
    }

    return [
      {
        accessor: "name",
        fixed: "left",
        Header: "Device/time",
        resizable: false,
        width: props.fixedColumnWidth ?? 225,
        Cell: (row: RowInfo) => (
          <div
            style={{
              color: row.original.isInactive ? "#ff1313" : "",
              display: "flex",
              flexDirection: "column",
              fontSize: "0.975rem",
              height: "100%",
              justifyContent: "center",
            }}
          >
            <div>{row.original.name}</div>
            {row.original.isInactive && (
              <div
                style={{
                  fontSize: "0.7rem",
                  lineHeight: 1.35,
                  marginTop: "0.25rem",
                  paddingLeft: "0.75rem",
                }}
              >
                *This device is already inactive. Please select a new device.
              </div>
            )}
          </div>
        ),
      },
      ...timeArray.map((time) => ({
        accessor: time,
        Header: time,
        minWidth: props.columnWidth ?? 125,
        resizable: false,
        style: { paddingBottom: "1rem", paddingTop: "1rem" },
        Cell: (row: RowInfo) => {
          const { original } = row;
          const [slotStartTime] = time.split(" - ");
          const referenceDate = formatDateToYYYYMMDD(filter.date, "-", true);
          const baseMoment = moment(`${referenceDate} ${slotStartTime}`);
          const slotsCount = 60 / original.time_per_slot;

          const deviceSlots = Array.from({ length: slotsCount }, (unused, index) => {
            const slotStart = baseMoment.clone().add(index * original.time_per_slot, "minutes");

            return {
              device: original.id,
              device_free: original.device_free,
              end_datetime: slotStart
                .clone()
                .add(original.time_per_slot, "minutes")
                .format("YYYY-MM-DD HH:mm"),
              start_datetime: slotStart.format("YYYY-MM-DD HH:mm"),
            };
          });

          const dataStart = moment(`${referenceDate} ${original.start_time}`);
          const dataEnd = moment(`${referenceDate} ${original.end_time}`);

          const filteredSlots = deviceSlots.filter(
            (slot) =>
              moment(slot.start_datetime).isSameOrAfter(dataStart) &&
              moment(slot.end_datetime).isSameOrBefore(dataEnd)
          );

          return (
            <div style={{ display: "grid", gap: "4px", gridTemplateColumns: "repeat(3,auto)" }}>
              {Array.from({ length: Math.max(3, filteredSlots.length) }, (unused, index) =>
                Object.keys(filteredSlots[index] || {}).length === 0 ? (
                  <div key={`time-per-slot-${index}`} style={{ minWidth: "25px", width: "100%" }}>
                    {"\u00A0"}
                  </div>
                ) : (
                  <DeviceTimeSlot
                    key={`time-per-slot-${index}`}
                    data={filteredSlots[index] || {}}
                    device={original}
                    rehabDeviceSlot={props.rehabDeviceSlot}
                    selectedDevices={selectedDevices}
                    onSelectDevice={handleSelectDevice}
                  />
                )
              )}
            </div>
          );
        },
      })),
    ];
  }, [
    filter.date,
    filteredDevices,
    handleSelectDevice,
    props.columnWidth,
    props.fixedColumnWidth,
    props.rehabDeviceSlot?.rehab_order_device,
  ]);

  const handleChangeFilter = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name = data.name;
    const value = data.type === "checkbox" ? data.checked : data.value;

    if (!name) {
      return;
    }

    // จะกรอง selectedDevices เอาเฉพาะ device ที่ไม่ฟรีออก
    if (name === "isDeviceFree" && !value) {
      const deviceList = selectedDevices.filter((device) => !device.device_free);

      setSelectedDevices([...deviceList]);
    }
    // จะกรอง selectedDevices ให้เหลือเฉพาะอันที่เลือก หรือ device_free เท่านั้น
    else if (name === "devices" && Array.isArray(value)) {
      const deviceList = selectedDevices.filter(
        (device) => value.includes(device.device) || device.device_free
      );

      setSelectedDevices([...deviceList]);
    }
    // ต้อง clear devices ที่เลือกทั้งหมด
    else if (["appointment", "date", "division", "type"].includes(name)) {
      filter.devices = []; // reset filter.devices ด้วย
      filter.isDeviceFree = false;

      if (name !== "appointment") {
        filter.appointment = null; // ถ้าเปลี่ยน field อื่นที่ไม่ใช่ appointment -> ต้องเคลียร์ค่า appointment ด้วย
      }

      if (["appointment", "type"].includes(name)) {
        props.setProp("selectedRehabDeviceSlot", null);
      }

      setSelectedDevices([]);
    }

    setFilter(() => ({ ...filter, [name]: value }));
  };

  const handleMakeAppointment = () => {
    const isSomeInactive = formattedDevices.some((item) => item.isInactive);

    if (isSomeInactive) {
      props.setProp(
        `errorMessage.${ACTION_SELECT_COMBINE}`,
        "You have selected a device that is already inactive. Please select a new device."
      );

      return;
    }

    setOpenModMakeAppointment(true);
  };

  const handleCloseModMakeAppointment = () => {
    setOpenModMakeAppointment(false);
  };

  const handleSuccess = (data: any) => {
    handleCloseModMakeAppointment();

    props.onSuccess(data);
  };

  const handleChangeAppointment = (e: SyntheticEvent, data: DropdownProps) => {
    const foundOption = appointmentOptions.find((option) => option.value === data.value);

    handleChangeFilter(e, data);

    props.onEvent({
      message: "selectRehabCombineAppointment",
      params: {
        ...foundOption,
        btnAction: ACTION_SELECT_COMBINE,
        checkedList: props.checkedAppointmentList,
      },
    });
  };

  console.log("CardSelectAppointmentDatetime props:", props, selectedDevices);

  return (
    <>
      <SnackMessage
        error={props.errorMessage?.[ACTION_SELECT_COMBINE]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${ACTION_SELECT_COMBINE}`, null);
        }}
      />

      <CardSelectAppointmentDatetimeUX
        appointmentLoading={!!props.buttonLoadCheck?.[ACTION_SELECT_COMBINE]}
        currentDate={currentDate}
        enabledDeviceFree={enabledDeviceFree}
        filter={filter}
        hideCombine={props.hideCombine}
        hideCreateNew={props.hideCreateNew}
        hideHeader={props.hideHeader}
        showSelectDivision={props.showSelectDivision}
        languageUX={props.languageUX}
        appointmentOptions={appointmentOptions}
        deviceOptions={deviceOptions}
        divisionOptions={props.divisionOptions}
        onChangeAppointment={handleChangeAppointment}
        onChangeFilter={handleChangeFilter}
        onClose={props.onClose}
        onMakeAppointment={handleMakeAppointment}
        deviceDropdown={
          <DropdownOptions
            name="devices"
            value={filter.devices}
            fluid
            inline
            multiple
            search
            options={deviceOptions}
            onChange={handleChangeFilter}
          />
        }
        disabledMakeAppointment={
          selectedDevices.length === 0 ||
          (filter.type === "COMBINE" && !props.selectedRehabDeviceSlot?.order_id)
        }
        table={
          formattedDevices.length === 0 ? (
            <div
              style={{ color: "rgba(199, 199, 199, 1)", padding: "3rem 0", textAlign: "center" }}
            >
              Please select device to choose the appointment
            </div>
          ) : (
            <ReactTableFixedColumns
              // @ts-ignore
              className="SelectAppDatetime"
              style={props.tableStyle}
              columns={columns as any[]}
              data={formattedDevices}
              minRows={1}
              showPagination={false}
            />
          )
        }
      />

      <ModMakeAppointmentRehab
        onEvent={props.onEvent}
        setProp={props.setProp}
        proxyController={props.proxyController}
        checkedAppointmentList={props.checkedAppointmentList}
        divisionId={filter.division}
        open={openModMakeAppointment}
        rehabDeviceSlot={props.rehabDeviceSlot}
        rehabTreatmentList={props.rehabTreatmentList}
        selectedDevices={selectedDevices}
        selectedRehabDeviceSlot={props.selectedRehabDeviceSlot}
        buttonLoadCheck={props.buttonLoadCheck}
        errorMessage={props.errorMessage}
        languageUX={props.languageUX}
        searchedItemListWithKey={props.searchedItemListWithKey}
        selectedPatient={props.selectedPatient}
        onClose={handleCloseModMakeAppointment}
        onSuccess={handleSuccess}
      />
    </>
  );
};

CardSelectAppointmentDatetime.displayName = "CardSelectAppointmentDatetime";

export default React.memo(CardSelectAppointmentDatetime);
